import { iasClient } from '../config/http.client';

export const ADMIN_DOMAINS = {
  rbac: {
    key: 'rbac',
    label: 'RBAC',
    basePath: '/rbac/rbac',
    listKey: 'roles',
    idField: 'RbacId',
    tableColumns: ['RbacId', 'RoleName', 'Description', 'Status', 'AccountId', 'ApplicationId', 'Components'],
    formFields: [
      { name: 'RoleName', label: 'Role Name', required: true, type: 'text' },
      { name: 'Description', label: 'Description', required: false, type: 'text' },
      { name: 'Status', label: 'Status', required: true, type: 'number', defaultValue: 1 },
      { name: 'AccountId', label: 'Account ID', required: false, type: 'text' },
      { name: 'ApplicationId', label: 'Application ID', required: false, type: 'text' },
      { name: 'DefaultRole', label: 'Default Role', required: false, type: 'boolean' },
      {
        name: 'Components',
        label: 'Components',
        required: false,
        type: 'components_table',
        defaultValue: [],
        componentSource: 'components',
      },
    ],
  },
  components: {
    key: 'components',
    label: 'Components',
    basePath: '/component/component',
    listKey: 'components',
    idField: 'ComponentId',
    tableColumns: ['ComponentId', 'ComponentName', 'Description', 'Status'],
    formFields: [
      { name: 'ComponentId', label: 'Component ID', required: false, type: 'text' },
      { name: 'ComponentName', label: 'Component Name', required: true, type: 'text' },
      { name: 'Description', label: 'Description', required: false, type: 'text' },
      { name: 'ComponentType', label: 'Component Type', required: false, type: 'number', defaultValue: 1 },
      { name: 'Status', label: 'Status', required: false, type: 'number', defaultValue: 1 },
    ],
  },
  users: {
    key: 'users',
    label: 'Users',
    basePath: '/user-management/user',
    listKey: 'users',
    idField: 'UserId',
    tableColumns: ['UserId', 'Email', 'Name', 'Description', 'Status', 'ModifiedDate'],
    formFields: [
      { name: 'Name', label: 'Name', required: true, type: 'text' },
      { name: 'Email', label: 'Email', required: true, type: 'text' },
      { name: 'Description', label: 'Description', required: true, type: 'text' },
      { name: 'PhoneNo', label: 'Phone', required: false, type: 'text' },
      { name: 'Status', label: 'Status', required: false, type: 'number', defaultValue: 1 },
    ],
  },
  accounts: {
    key: 'accounts',
    label: 'Accounts',
    basePath: '/account/account',
    listKey: 'accounts',
    idField: 'AccountId',
    tableColumns: ['AccountId', 'Name', 'AccountName', 'Description', 'Status', 'ModifiedDate'],
    formFields: [
      { name: 'Name', label: 'Name', required: true, type: 'text' },
      { name: 'AccountName', label: 'Account Name', required: true, type: 'text' },
      { name: 'Description', label: 'Description', required: false, type: 'text' },
      { name: 'Address', label: 'Address (JSON)', required: false, type: 'json', defaultValue: {} },
      { name: 'PhoneNumber', label: 'Phone Number', required: false, type: 'text' },
      { name: 'AccountCategory', label: 'Account Category', required: false, type: 'text' },
      { name: 'Status', label: 'Status', required: false, type: 'number', defaultValue: 1 },
    ],
  },
  applications: {
    key: 'applications',
    label: 'Applications',
    basePath: '/application/application',
    listKey: 'applications',
    idField: 'ApplicationId',
    tableColumns: ['ApplicationId', 'ApplicationName', 'Category', 'Description', 'Status', 'ModifiedDate'],
    formFields: [
      { name: 'ApplicationName', label: 'Application Name', required: true, type: 'text' },
      { name: 'ApplicationId', label: 'Application ID', required: false, type: 'text' },
      { name: 'Description', label: 'Description', required: false, type: 'text' },
      { name: 'Category', label: 'Category', required: true, type: 'number', defaultValue: 0 },
      { name: 'Status', label: 'Status', required: false, type: 'number', defaultValue: 1 },
    ],
  },
  userAssociations: {
    key: 'userAssociations',
    label: 'User Associations',
    basePath: '/account/userassociation',
    listKey: 'userAssociation',
    idField: 'UserAssociationId',
    tableColumns: [
      'UserAssociationId',
      'UserId',
      'UserEmail',
      'AccountId',
      'AccountName',
      'ApplicationId',
      'ApplicationName',
      'RbacId',
      'RoleName',
      'Status',
    ],
    formFields: [
      { name: 'UserId', label: 'User', required: true, type: 'select', source: 'users' },
      { name: 'AccountId', label: 'Account', required: true, type: 'select', source: 'accounts' },
      { name: 'ApplicationId', label: 'Application', required: false, type: 'select', source: 'applications' },
      { name: 'RbacId', label: 'RBAC Role', required: true, type: 'select', source: 'rbac' },
      {
        name: 'Status',
        label: 'Status',
        required: true,
        type: 'select',
        defaultValue: 1,
        options: [
          { value: 1, label: 'Active' },
          { value: 0, label: 'Inactive' },
        ],
      },
    ],
  },
};

const clampTake = (take) => {
  const parsed = Number(take);
  if (Number.isNaN(parsed) || parsed <= 0) return 100;
  return Math.min(parsed, 500);
};

const extractList = (data, preferredKey) => {
  if (Array.isArray(data)) return data;
  if (preferredKey && Array.isArray(data?.[preferredKey])) return data[preferredKey];
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (data && typeof data === 'object') {
    const firstArrayKey = Object.keys(data).find((k) => Array.isArray(data[k]));
    if (firstArrayKey) return data[firstArrayKey];
  }
  return [];
};

export const listDomainRecords = async (domain, params = {}, basePathOverride = '') => {
  const config = ADMIN_DOMAINS[domain];
  if (!config) throw new Error(`Unsupported admin domain: ${domain}`);
  const basePath = basePathOverride || config.basePath;

  const query = {
    skip: Number(params.skip || 0),
    take: clampTake(params.take || 100),
    ...params,
  };

  let res;
  if (domain === 'userAssociations') {
    // Merge active + inactive for admin visibility.
    const allRes = await iasClient.get(basePath, { params: query });
    let inactiveRes = { data: [] };
    try {
      inactiveRes = await iasClient.get(basePath, { params: { ...query, status: 0 } });
    } catch {
      // Some IAS environments do not support status filter for this endpoint.
    }
    const all = extractList(allRes.data, config.listKey);
    const inactive = extractList(inactiveRes.data, config.listKey);
    const byId = new Map();
    [...all, ...inactive].forEach((row, idx) => {
      const key = row?.UserAssociationId || `${row?.AccountId || ''}#${row?.UserId || ''}#${idx}`;
      if (!byId.has(key)) byId.set(key, row);
    });
    return {
      records: Array.from(byId.values()),
      raw: allRes.data,
    };
  }

  res = await iasClient.get(basePath, { params: query });
  const records = extractList(res.data, config.listKey);
  return {
    records,
    raw: res.data,
  };
};

export const getDomainRecordById = async (domain, id, basePathOverride = '') => {
  const config = ADMIN_DOMAINS[domain];
  if (!config) throw new Error(`Unsupported admin domain: ${domain}`);
  if (!id) throw new Error('Record id is required');
  const basePath = basePathOverride || config.basePath;
  const res = await iasClient.get(`${basePath}/${id}`);
  return res.data;
};

export const createDomainRecord = async (domain, payload, basePathOverride = '') => {
  const config = ADMIN_DOMAINS[domain];
  if (!config) throw new Error(`Unsupported admin domain: ${domain}`);
  const basePath = basePathOverride || config.basePath;
  if (domain === 'userAssociations') {
    const accountId = payload?.AccountId;
    const userId = payload?.UserId;
    if (!accountId || !userId) throw new Error('Account and User are required');
    const body = {
      UserId: userId,
      RbacId: payload.RbacId,
      ...(payload.ApplicationId ? { ApplicationId: payload.ApplicationId } : {}),
      ...(payload.Status !== undefined && payload.Status !== null ? { Status: Number(payload.Status) } : {}),
    };
    const res = await iasClient.post(`/account/account/${accountId}/User`, body);
    return res.data;
  }

  const res = await iasClient.post(basePath, payload);
  return res.data;
};

export const updateDomainRecord = async (domain, id, payload, basePathOverride = '', recordContext = null) => {
  const config = ADMIN_DOMAINS[domain];
  if (!config) throw new Error(`Unsupported admin domain: ${domain}`);
  if (!id) throw new Error('Record id is required');
  const basePath = basePathOverride || config.basePath;
  if (domain === 'userAssociations') {
    const accountId = payload?.AccountId || recordContext?.AccountId;
    const userId = payload?.UserId || recordContext?.UserId;
    if (!accountId || !userId) throw new Error('AccountId and UserId are required for association update');
    const body = {
      UserAssociationId: payload?.UserAssociationId || recordContext?.UserAssociationId || `${accountId}#${userId}`,
      UserId: userId,
      AccountId: accountId,
      ApplicationId: payload?.ApplicationId || recordContext?.ApplicationId,
      RbacId: payload?.RbacId || recordContext?.RbacId,
      Status: payload?.Status !== undefined ? Number(payload.Status) : Number(recordContext?.Status ?? 1),
      ...(payload?.etag || recordContext?.etag ? { etag: payload?.etag || recordContext?.etag } : {}),
    };
    const res = await iasClient.put(`/account/account/${accountId}/User/${userId}`, body);
    return res.data;
  }

  const res = await iasClient.put(`${basePath}/${id}`, payload);
  return res.data;
};

export const deleteDomainRecord = async (domain, id, basePathOverride = '', recordContext = null) => {
  const config = ADMIN_DOMAINS[domain];
  if (!config) throw new Error(`Unsupported admin domain: ${domain}`);
  if (!id) throw new Error('Record id is required');
  const basePath = basePathOverride || config.basePath;
  if (domain === 'userAssociations') {
    const accountId = recordContext?.AccountId;
    const userId = recordContext?.UserId;
    if (!accountId || !userId) throw new Error('AccountId and UserId are required for association delete');
    const res = await iasClient.delete(`/account/account/${accountId}/User/${userId}`);
    return res.data;
  }

  const res = await iasClient.delete(`${basePath}/${id}`);
  return res.data;
};

const fetchLookupList = async (domainKey) => {
  const cfg = ADMIN_DOMAINS[domainKey];
  if (!cfg) return [];
  const result = await listDomainRecords(domainKey, { skip: 0, take: 500 });
  return result.records || [];
};

const toOptions = (domainKey, rows) => {
  if (domainKey === 'users') {
    return rows
      .filter((r) => r?.UserId)
      .map((r) => ({ value: r.UserId, label: r.Email ? `${r.Email} (${r.UserId})` : r.UserId }));
  }
  if (domainKey === 'accounts') {
    return rows
      .filter((r) => r?.AccountId)
      .map((r) => ({ value: r.AccountId, label: r.AccountName || r.Name || r.AccountId }));
  }
  if (domainKey === 'applications') {
    return rows
      .filter((r) => r?.ApplicationId)
      .map((r) => ({ value: r.ApplicationId, label: r.ApplicationName || r.ApplicationId }));
  }
  if (domainKey === 'rbac') {
    return rows
      .filter((r) => r?.RbacId)
      .map((r) => ({ value: r.RbacId, label: r.RoleName || r.RbacId }));
  }
  if (domainKey === 'components') {
    return rows
      .filter((r) => r?.ComponentId)
      .map((r) => ({ value: r.ComponentId, label: r.ComponentName ? `${r.ComponentName} (${r.ComponentId})` : r.ComponentId }));
  }
  return [];
};

export const getAdminDomainLookups = async () => {
  const settled = await Promise.allSettled([
    fetchLookupList('users'),
    fetchLookupList('accounts'),
    fetchLookupList('applications'),
    fetchLookupList('rbac'),
    fetchLookupList('components'),
  ]);

  const users = settled[0].status === 'fulfilled' ? settled[0].value : [];
  const accounts = settled[1].status === 'fulfilled' ? settled[1].value : [];
  const applications = settled[2].status === 'fulfilled' ? settled[2].value : [];
  const rbac = settled[3].status === 'fulfilled' ? settled[3].value : [];
  const components = settled[4].status === 'fulfilled' ? settled[4].value : [];

  return {
    users: toOptions('users', users),
    accounts: toOptions('accounts', accounts),
    applications: toOptions('applications', applications),
    rbac: toOptions('rbac', rbac),
    components: toOptions('components', components),
  };
};
